import { AccountEndpoint } from './account-endpoint.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, forkJoin, Subject } from 'rxjs';
import { catchError, map, mergeMap, tap, timeout } from 'rxjs/operators';
import { Role } from 'src/app/models/role';
import { User } from 'src/app/models/user';
import { UserEdit } from 'src/app/models/user-edit.model';
import { Global } from '../../global.constants';
import { Utility } from '../../utility';
import { AuthService } from '../auth/auth.service';
import { DBkeys } from '../auth/db-keys';
import { ReadConfigService } from '../common/read-config.service';

export type RolesChangedOperation = 'add' | 'delete' | 'modify';
export interface RolesChangedEventArg { roles: Role[] | string[]; operation: RolesChangedOperation; }

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  public static readonly roleAddedOperation: RolesChangedOperation = 'add';
  public static readonly roleDeletedOperation: RolesChangedOperation = 'delete';
  public static readonly roleModifiedOperation: RolesChangedOperation = 'modify';
  Claims: any[];
  private rolesChanged = new Subject<RolesChangedEventArg>();

  constructor(private http: HttpClient,
    private readConfigService: ReadConfigService,
    private accountEndpoint: AccountEndpoint,
    private authService: AuthService) { }

  readonly rootUrl = this.readConfigService.getApiUrl();
  apiTimeout = this.readConfigService.getApiTimeout();
  readonly httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
  reqNoAuthHeader = new HttpHeaders({ 'No-Auth': 'True' });

  UserInfo(): any {
    const userInfo = {
      encryptOrg: Utility.removeQuotes(sessionStorage.getItem(DBkeys.ENC_ORGID)),
      encryptUsr: Utility.removeQuotes(sessionStorage.getItem(DBkeys.ENC_USER))
    };
    return userInfo;
  }

  // UPLOAD APP CALLS START
  getUser(userId?: string) {
    return this.accountEndpoint.getUserEndpoint(userId)
      .pipe(map((response: any) => response as User));
  }

  getUserAndRoles(userId?: string) {
    return forkJoin(
      this.accountEndpoint.getUserEndpoint(userId).pipe(map((response: any) => response as User)),
      this.accountEndpoint.getRolesEndpoint().pipe(map((response: any) => response as Role[])));
  }

  getUsers(page?: number, pageSize?: number) {
    return this.accountEndpoint.getUsersEndpoint()
      .pipe(map((response: any) => response as User[]));
  }

  getUsersAndRoles(page?: number, pageSize?: number) {
    return forkJoin(
      this.accountEndpoint.getUsersEndpoint().pipe(map((response: any) => response as User[])),
      this.accountEndpoint.getRolesEndpoint().pipe(map((response: any) => response as Role[])));
  }

  updateUser(user: any) {
    if (user.id) {
      return this.accountEndpoint.getUpdateUserEndpoint(user, user.id);
    } else {
      return this.accountEndpoint.getUserByUserNameEndpoint(user.userName)
        .pipe(map((response: any) => response as User))
        .pipe(mergeMap(foundUser => {
          user.id = foundUser.Id;
          return this.accountEndpoint.getUpdateUserEndpoint(user, user.id);
        }));
    }
  }

  newUser(user: UserEdit) {
    return this.accountEndpoint.getNewUserEndpoint(user)
      .pipe(map((response: any) => response as User));
  }

  getUserPreferences() {
    return this.accountEndpoint.getUserPreferencesEndpoint()
      .pipe(map((response: any) => response as string));
  }

  updateUserPreferences(configuration: string | null) {
    const objConf = configuration == null ? '' : configuration;
    return this.accountEndpoint.getUpdateUserPreferencesEndpoint(objConf);
  }

  unblockUser(userId: string) {
    return this.accountEndpoint.getUnblockUserEndpoint(userId);
  }

  getRoles(page?: number, pageSize?: number) {
    return this.accountEndpoint.getRolesEndpoint(page, pageSize)
      .pipe(map((response: any) => response as Role[]));
  }

  newRole(role: Role) {
    return this.accountEndpoint.getNewRoleEndpoint(role)
      .pipe(map((response: any) => response as Role),
        tap((data: any) => this.onRolesChanged([role], AccountService.roleAddedOperation)));
  }

  private onRolesChanged(roles: Role[] | string[], op: RolesChangedOperation) {
    this.rolesChanged.next({ roles, operation: op });
  }

  onRolesUserCountChanged(roles: Role[] | string[]) {
    return this.onRolesChanged(roles, AccountService.roleModifiedOperation);
  }

  getRolesChangedEvent(): Observable<RolesChangedEventArg> {
    return this.rolesChanged.asObservable();
  }

  get currentUser() {
    return this.authService.currentUser;
  }

  get currentPermissions(): any {
    return this.authService.currentPermissions;
  }

  // UPLOAD APP CALLS ENDS

  getCurrentUser(): Observable<any> {
    const userInfo = this.UserInfo();
    const obj = { userInfo };
    const url = this.rootUrl + Global.BASE_GET_CURRENT_USER_ENDPOINT;
    return this.http.post<any>(url, obj, this.httpOptions)
      .pipe(this.handleError());
  }

  updateUserDetail(user: any): Observable<any> {
    const userInfo = this.UserInfo();
    const obj = { userInfo, user };
    const url = this.rootUrl + Global.BASE_UPDATE_CURRENT_USER_ENDPOINT;
    return this.http.post<any>(url, obj, this.httpOptions)
      .pipe(this.handleError());
  }

  handleError() {
    return timeout(this.apiTimeout),
      catchError((err: any) => {
        return throwError(err || 'Server error');
      }),
      map((v: any) => {
        if (v) {
          return v;
        }
      });
  }
}
